import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
  const { authed, loading, login } = useAdminAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#010204] text-[#C9C6C6]">
        Loading…
      </div>
    );
  }
  if (authed) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter the admin password");
      return;
    }
    setBusy(true);
    try {
      await login(password);
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#010204] text-[#EEEEEF] flex flex-col">
      <header className="px-6 lg:px-10 h-16 flex items-center justify-between border-b border-[#C9C6C6]/10">
        <Link to="/" className="flex items-center gap-3">
          <img src="/brand/TPWH_Logo_Transparent.png" alt="The Pros Work Hub" className="h-9 w-auto shrink-0" data-testid="brand-logo-img" />
          <span className="font-mono-alt uppercase tracking-[0.25em] text-xs text-[#0E93EC] font-semibold">
            Admin
          </span>
        </Link>
        <Link
          to="/"
          className="text-[11px] uppercase tracking-[0.22em] text-[#C9C6C6] hover:text-white"
        >
          Back to site
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
          data-testid="admin-login-page"
        >
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-4">
            — Admin access
          </div>
          <h1 className="font-editorial text-5xl md:text-6xl font-light tracking-tighter leading-[0.95]">
            Verify.
            <br />
            <span className="italic">Approve.</span>
          </h1>
          <p className="mt-6 text-[#C9C6C6] max-w-sm leading-relaxed">
            Restricted area. Sign in with the admin password to review
            contractor verifications.
          </p>

          <div className="mt-10">
            <Label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
              Password
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 bg-transparent border-x-0 border-t-0 border-b border-[#C9C6C6]/25 rounded-none h-12 text-white placeholder:text-[#C9C6C6] focus-visible:ring-0 focus-visible:border-[#0E93EC] px-0"
              data-testid="admin-password-input"
              autoFocus
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="group mt-8 w-full bg-[#0E93EC] hover:bg-[#0E2F5F] text-[#010204] rounded-none h-14 pl-6 pr-5 text-base font-semibold"
            data-testid="admin-login-submit"
          >
            <span className="flex-1 text-left uppercase tracking-[0.18em] text-sm">
              {busy ? "Signing in…" : "Sign in"}
            </span>
            <span className="inline-flex items-center justify-center w-9 h-9 bg-[#010204] text-[#0E93EC] transition-transform group-hover:translate-x-1">
              <ArrowRight className="w-4 h-4" />
            </span>
          </Button>
        </motion.form>
      </div>
    </div>
  );
}
